/* global React, Icon, t */
// ============================================================
// PLUMME — Shared block renderer for help articles
// Used by the drawer reader (help-articles.jsx) and the Support > Articles
// page (support-articles-body.jsx). Blocks are {t:'heading'|'para'|'step'|'figure'|'tip'}.
// Para/step/tip text may carry inline `spans` (bold, italic, underline, http(s) link);
// `text` is the plain fallback. Text is rendered as React text nodes only.
// ============================================================
(function () {
  const TIP_ICON = { info: 'info', tip: 'sparkle', warn: 'alertTri' };

  // Only absolute http(s) URLs become links; anything else (javascript:, data:, relative) is plain text.
  const safeLink = (value) => {
    if (typeof value !== 'string') return '';
    try {
      const url = new URL(value.trim());
      return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : '';
    } catch (error) {
      return '';
    }
  };

  function HelpRichText({ text, spans }) {
    const usable = Array.isArray(spans) && spans.length > 0 && spans.every((span) => span && typeof span.text === 'string');
    if (!usable) return text;
    return spans.map((span, index) => {
      let node = span.text;
      if (span.bold) node = <strong>{node}</strong>;
      if (span.italic) node = <em>{node}</em>;
      if (span.underline) node = <u>{node}</u>;
      const href = safeLink(span.link);
      if (href) node = <a className="ha-link" href={href} target="_blank" rel="noopener noreferrer">{node}</a>;
      return <React.Fragment key={index}>{node}</React.Fragment>;
    });
  }

  // variant 'drawer': h2 headings, figures open the lightbox (onExpandFigure).
  // variant 'page': h3 headings, decorative figure images, aria-hidden step numbers/icons.
  function HelpArticleBlocks({ blocks, variant = 'drawer', onExpandFigure }) {
    const page = variant === 'page';
    const Heading = page ? 'h3' : 'h2';
    return (blocks || []).map((b, i) => {
      if (b.t === 'heading') return <Heading key={i} className="ha-h2">{b.text}</Heading>;
      if (b.t === 'para') return <p key={i} className="ha-para"><HelpRichText text={b.text} spans={b.spans} /></p>;
      if (b.t === 'step') return <div key={i} className="ha-step">
        <span className="ha-step-n" aria-hidden={page ? 'true' : undefined}>{b.n}</span>
        <div className="ha-step-body"><div className="ha-step-title">{b.title}</div><p className="ha-step-text"><HelpRichText text={b.text} spans={b.spans} /></p></div>
      </div>;
      if (b.t === 'figure') {
        if (!b.src) return null;
        const dots = <span className="ha-dots" aria-hidden="true"><i /><i /><i /></span>;
        if (page) return <figure key={i} className="ha-figure">
          <div className="ha-figure-frame">{dots}<img src={b.src} alt={b.caption ? '' : (b.alt || '')} loading="lazy" /></div>
          {b.caption && <figcaption>{b.caption}</figcaption>}
        </figure>;
        return <figure key={i} className="ha-figure">
          <button type="button" className="ha-figure-frame" onClick={() => onExpandFigure?.(b)} aria-label={`${t('ha.expandImage')}: ${b.caption}`} title={t('ha.expandImage')}>{dots}<img src={b.src} alt={b.caption} loading="lazy" /></button>
          <figcaption>{b.caption}</figcaption>
        </figure>;
      }
      if (b.t === 'tip') {
        const kind = TIP_ICON[b.kind] ? b.kind : 'info';
        return <div key={i} className={'ha-tip ha-tip-' + kind}>
          <span className="ha-tip-ico" aria-hidden={page ? 'true' : undefined}><Icon name={TIP_ICON[kind]} size={18} /></span>
          <div><div className="ha-tip-title">{b.title}</div><p className="ha-tip-text"><HelpRichText text={b.text} spans={b.spans} /></p></div>
        </div>;
      }
      return null;
    });
  }

  Object.assign(window, { HelpRichText, HelpArticleBlocks, PlummeHelpArticleLinks: { safeLink } });
})();
